/**
 * Placing the table changes of an apply result in a stored document.
 *
 * `TableOperations.apply` has already checked every operation against the
 * reader's list, so this writer only has to find each named paragraph in the
 * document, resolve its row, and call the cloner. What it adds is order: rows
 * asked for after the same template row by separate operations land in the
 * order asked, and tables asked for after the same paragraph do too, through
 * the same tail map `ProposalBuilder.placeInsertions` keeps for paragraphs.
 *
 * Deletions are written first, because a struck row keeps its place and its
 * paragraphs, so nothing an insertion anchors on moves.
 */
import { OoxmlRange } from "./ooxml-range";
import { Revision } from "./ooxml/revision";
import { TableRowXml } from "./ooxml/table-row-xml";
import { TableStructure } from "./ooxml/table-structure";
import type { TableOperationsApplyResult } from "./table-operations";

export interface TableEditDocumentWriteRequest {
    /** The document as `ProposalBuilder` left it, package or bare `w:document`. */
    documentXml: string;
    edits: TableOperationsApplyResult;
    revision: Revision;
}

export interface TableEditDocumentWriteOutcome {
    documentXml: string;
    changeCount: number;
}

export interface TableEditWriteRequest {
    bodyRoot: Element;
    /** The body paragraph element a bookmark names, or null when the document holds no such paragraph. */
    paragraphOfBookmark: (bookmark: string) => Element | null;
    edits: TableOperationsApplyResult;
    /** Author and date of every mark; `w:id` values continue from here, one per stamped element. */
    revision: Revision;
}

export interface TableEditWriteOutcome {
    /** Rows inserted plus rows deleted plus tables inserted. */
    changeCount: number;
    /** Every new paragraph written, in the order written; each is unnamed until the next read names it. */
    insertedParagraphElements: Element[];
}

/** Thrown when a paragraph the checked operations name is not in the document being written, or is no longer in a table: the reader's list came from another version of it. */
export class StaleTableEditError extends Error {
    constructor(mismatch: string) {
        super(`${mismatch}; read the document again before applying.`);
        this.name = "StaleTableEditError";
    }
}

export class TableEditWriter {
    /**
     * The string entry the section route uses: parses the document, resolves
     * each bookmark to the paragraph that answers to it in the document being
     * written, writes the changes, serializes. Runs after `ProposalBuilder`, on
     * the document it left.
     *
     * WHY the bookmark is resolved in this document and not in the reader's list
     *   The list was read before `ProposalBuilder` ran, and the builder returns
     *   one extra body paragraph for every `insert_paragraph_after` in the same
     *   operation list. An index from the list would then name the paragraph
     *   next door, so one answer that adds a paragraph and a table row could
     *   never be written. The names the list carries are the names the document
     *   still carries, so the name is what resolves.
     *
     * WHEN there is no table change
     *   CONSEQUENCE
     *     The document string comes back as given; nothing is parsed or re-serialized.
     */
    static writeDocumentXml(request: TableEditDocumentWriteRequest): TableEditDocumentWriteOutcome {
        const nothingToWrite = request.edits.rowInsertions.length === 0 && request.edits.rowDeletions.length === 0 && request.edits.tableInsertions.length === 0;
        if (nothingToWrite) return { documentXml: request.documentXml, changeCount: 0 };
        const range = new OoxmlRange(request.documentXml);
        const paragraphElementByBookmarkName = range.paragraphElementsByBookmarkName();
        const paragraphOfBookmark = (bookmark: string): Element | null => paragraphElementByBookmarkName.get(bookmark) ?? null;
        const firstParagraph = range.paragraphElement(0);
        /**
         * WHEN the document has no body paragraph at all
         *   CONSEQUENCE
         *     No bookmark can resolve, so every change is stale.
         */
        if (firstParagraph === undefined) throw new StaleTableEditError("The document being written has no paragraph");
        const outcome = TableEditWriter.write({ bodyRoot: firstParagraph.ownerDocument.documentElement, paragraphOfBookmark, edits: request.edits, revision: request.revision });
        return { documentXml: range.serialize(), changeCount: outcome.changeCount };
    }

    static write(request: TableEditWriteRequest): TableEditWriteOutcome {
        const structure = new TableStructure(request.bodyRoot);
        const insertedParagraphElements: Element[] = [];
        let changeCount = 0;
        for (const deletion of request.edits.rowDeletions) {
            TableRowXml.deleteRow({ row: TableEditWriter.rowOf(structure, request, deletion.rowOfBookmark), revision: request.revision });
            changeCount += 1;
        }
        /**
         * EXAMPLE
         *   two operations both after the row of "_premium_cell_one", rows [A] then [B]
         *   first  -> tails: template row -> A;  A sits after the template
         *   second -> placed after A, tails: template row -> B; order in the table: template, A, B
         */
        const lastRowPlacedAfterTemplate = new Map<Element, Element>();
        for (const insertion of request.edits.rowInsertions) {
            const templateRow = TableEditWriter.rowOf(structure, request, insertion.afterRowOfBookmark);
            const inserted = TableRowXml.insertRowsAfter({
                structure,
                templateRow,
                afterRowElement: lastRowPlacedAfterTemplate.get(templateRow.rowElement) ?? templateRow.rowElement,
                rows: insertion.rows,
                mergedCells: insertion.mergedCells,
                revision: request.revision,
            });
            lastRowPlacedAfterTemplate.set(templateRow.rowElement, inserted.rowElements[inserted.rowElements.length - 1]);
            insertedParagraphElements.push(...inserted.paragraphElements);
            changeCount += inserted.rowElements.length;
        }
        const lastElementPlacedAfterParagraph = new Map<Element, Element>();
        for (const tableInsertion of request.edits.tableInsertions) {
            const anchorParagraph = TableEditWriter.paragraphOf(request, tableInsertion.afterBookmark);
            const inserted = TableRowXml.insertTableAfter({ afterParagraph: lastElementPlacedAfterParagraph.get(anchorParagraph) ?? anchorParagraph, rows: tableInsertion.rows, place: tableInsertion.place, revision: request.revision });
            /**
             * CASES, for what the next table after the same paragraph follows
             *   - the cloner added a trailing paragraph -> that paragraph, so the two tables stay separated
             *   - it did not                             -> the table itself; a paragraph already follows it
             */
            const trailingParagraph = inserted.paragraphElements[inserted.paragraphElements.length - 1];
            const trailingParagraphFollowsTable = trailingParagraph !== undefined && trailingParagraph.parentNode === inserted.tableElement.parentNode;
            lastElementPlacedAfterParagraph.set(anchorParagraph, trailingParagraphFollowsTable ? trailingParagraph : inserted.tableElement);
            insertedParagraphElements.push(...inserted.paragraphElements);
            changeCount += 1;
        }
        return { changeCount, insertedParagraphElements };
    }

    /**
     * WHEN the named paragraph is in the document but outside every table
     *   CONSEQUENCE
     *     Refuse as stale: the reader's list said it was a cell, so the document has changed since.
     */
    private static rowOf(structure: TableStructure, request: TableEditWriteRequest, bookmark: string) {
        const row = structure.rowOf(TableEditWriter.paragraphOf(request, bookmark));
        if (row === null) throw new StaleTableEditError(`The paragraph "${bookmark}" is not in a table of the document being written`);
        return row;
    }

    private static paragraphOf(request: TableEditWriteRequest, bookmark: string): Element {
        const paragraph = request.paragraphOfBookmark(bookmark);
        if (paragraph === null) throw new StaleTableEditError(`The paragraph "${bookmark}" is not in the document being written`);
        return paragraph;
    }
}
